import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  Divider,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NfcIcon from '@mui/icons-material/Nfc';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { clubEntryService, AssignCardsDto, CardAssignmentItemDto } from '../../../../../services/clubEntryService';
import { linkClubEntryCard } from '../../hooks/fiscalDevices/pavopay';
import { cardManagementService } from '../../../../../services/cardManagementService';
import { useAuth } from '../../../../../contexts/AuthContext';

interface CartItem {
  id: string;
  productId: string;
  name: string;
  salePrice: number;
  quantity: number;
  itemType?: string;
}

interface EntitlementForModal {
  productId: string;
  productName: string;
  quantity: number;
  unitPricePaid: number;
}

interface CardSlotForModal {
  serviceItem?: { id: string; name: string; price: number; quantity: number };
  packageName?: string;
  entitlements?: EntitlementForModal[];
}

interface CardTypeInfo {
  id: number;
  name: string;
  depositAmount: number;
  color?: string;
}

interface PostSaleCardAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  saleReceiptId: number;
  saleReceiptNumber?: string;
  pavoPaySaleId?: number;
  pavoPaySaleNumber?: string;
  screenId?: number;
  paymentMethod: string;
  serviceItems: CartItem[];
  /** Kulüp girişi paket-instance kart slotları (giriş hizmeti + entitlement'lar). */
  cardSlots?: CardSlotForModal[];
  companyId?: string;
  currentUserEmail?: string;
  depositAmount: number;
  depositEnabled: boolean;
  /** Ekran ayarındaki geçiş modu: 'SINGLE_ENTRY' | 'UNLIMITED_DAILY'. Verilmezse SINGLE_ENTRY. */
  entryMode?: string;
  onComplete?: () => void;
}

interface ScannedCard {
  cardUID: string;
  cardLabel: string;
  assignedServiceItem?: CartItem;
  cardTypeName?: string;
  resolvedDepositAmount: number;
  packageName?: string;
  entitlements?: EntitlementForModal[];
  /** Bu karta atanan kart slotu (cardSlots index'i). Operatör elle değiştirebilir. */
  assignedSlotIndex?: number;
}

const PostSaleCardAssignmentModal: React.FC<PostSaleCardAssignmentModalProps> = ({
  open,
  onClose,
  saleReceiptId,
  saleReceiptNumber,
  pavoPaySaleId,
  pavoPaySaleNumber,
  screenId,
  paymentMethod,
  serviceItems,
  cardSlots,
  companyId: companyIdProp,
  currentUserEmail,
  depositAmount,
  depositEnabled,
  entryMode,
  onComplete,
}) => {
  const { currentUser } = useAuth();
  const companyId = companyIdProp || currentUser?.companyId;
  const [scannedCards, setScannedCards] = useState<ScannedCard[]>([]);
  const [cardInput, setCardInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [cardTypes, setCardTypes] = useState<CardTypeInfo[]>([]);

  const slotCount = cardSlots?.length || 0;
  const expectedCardCount = slotCount > 0
    ? slotCount
    : serviceItems.reduce((sum, item) => sum + item.quantity, 0);

  useEffect(() => {
    if (open) {
      setScannedCards([]);
      setCardInput('');
      setError(null);
      setSuccess(false);
      setScanning(false);

      if (depositEnabled && companyId) {
        cardManagementService.getCardTypes(companyId).then(res => {
          const types = (res.data?.data || res.data || []).map((t: any) => ({
            id: t.id || t.Id,
            name: t.name || t.Name,
            depositAmount: t.depositAmount ?? t.DepositAmount ?? 0,
            color: t.color || t.Color,
          }));
          setCardTypes(types);
        }).catch(err => {
          console.warn('[PostSaleCardModal] Kart tipleri yüklenemedi:', err);
        });
      }
    }
  }, [open, depositEnabled, companyId]);

  const resolveCardDeposit = useCallback(async (uid: string): Promise<{ depositAmt: number; typeName?: string }> => {
    if (!depositEnabled) return { depositAmt: 0 };
    if (!companyId) return { depositAmt: depositAmount };

    try {
      const res = await cardManagementService.getCardByUID(companyId, uid);
      const card = res.data?.data || res.data;
      if (card) {
        const cardTypeId = card.cardTypeId || card.CardTypeId;
        const matchedType = cardTypes.find(t => t.id === cardTypeId);
        if (matchedType && matchedType.depositAmount > 0) {
          return { depositAmt: matchedType.depositAmount, typeName: matchedType.name };
        }
      }
    } catch {
      // Kart bulunamadı, varsayılan deposito kullan
    }
    return { depositAmt: depositAmount };
  }, [depositEnabled, companyId, cardTypes, depositAmount]);

  const handleScanCard = useCallback(async () => {
    const uid = cardInput.trim();
    if (!uid) return;

    if (scannedCards.some(c => c.cardUID === uid)) {
      setError('Bu kart zaten okutuldu');
      setCardInput('');
      return;
    }

    if (scannedCards.length >= expectedCardCount) {
      setError(`Maksimum ${expectedCardCount} kart okutulabilir`);
      setCardInput('');
      return;
    }

    setScanning(true);
    setCardInput('');
    setError(null);

    try {
      const { depositAmt, typeName } = await resolveCardDeposit(uid);

      let assignItem: CartItem | undefined;
      let entitlements: EntitlementForModal[] = [];
      let packageName: string | undefined;
      let assignedSlotIndex: number | undefined;

      if (cardSlots && cardSlots.length > 0) {
        // ✅ Paket slotları: İLK ATANMAMIŞ slotu bu karta ata (giriş hizmeti +
        // entitlement'lar). Operatör sonradan dropdown'dan değiştirebilir.
        const usedIdx = new Set(
          scannedCards.map(c => c.assignedSlotIndex).filter((i): i is number => i != null)
        );
        let slotIndex = cardSlots.findIndex((_, i) => !usedIdx.has(i));
        if (slotIndex < 0) slotIndex = scannedCards.length;
        const slot = cardSlots[slotIndex];
        assignedSlotIndex = slotIndex;
        if (slot?.serviceItem) {
          assignItem = {
            id: slot.serviceItem.id,
            productId: slot.serviceItem.id,
            name: slot.serviceItem.name,
            salePrice: slot.serviceItem.price,
            quantity: 1,
          };
        }
        entitlements = slot?.entitlements || [];
        packageName = slot?.packageName;
      } else {
        const unassignedItems = serviceItems.flatMap(item => {
          const assignedCount = scannedCards.filter(c => c.assignedServiceItem?.productId === item.productId).length;
          const remaining = item.quantity - assignedCount;
          return Array(Math.max(0, remaining)).fill(item);
        });
        assignItem = unassignedItems[0];
      }

      setScannedCards(prev => [
        ...prev,
        {
          cardUID: uid,
          cardLabel: `Kart #${prev.length + 1}`,
          assignedServiceItem: assignItem,
          cardTypeName: typeName,
          resolvedDepositAmount: depositAmt,
          entitlements,
          packageName,
          assignedSlotIndex,
        },
      ]);
    } catch (err) {
      setError('Kart bilgisi alınamadı');
    } finally {
      setScanning(false);
    }
  }, [cardInput, scannedCards, serviceItems, expectedCardCount, resolveCardDeposit, cardSlots]);

  const handleRemoveCard = (uid: string) => {
    setScannedCards(prev => prev.filter(c => c.cardUID !== uid));
  };

  // Bir slotu okutulan kart listesinde göstermek için etiket (paket adı + ürün hakları).
  const slotLabel = useCallback((slot: CardSlotForModal, index: number) => {
    const ents = (slot.entitlements || [])
      .map(e => `${e.productName}${e.quantity > 1 ? ` x${e.quantity}` : ''}`)
      .join(', ');
    const base = slot.packageName || slot.serviceItem?.name || `Paket ${index + 1}`;
    return ents ? `${base} → 🎁 ${ents}` : `${base} (ürün yok)`;
  }, []);

  // Operatör bir bilekliğe atanacak paketi/slotu değiştirir.
  // Hedef slot başka bir kartta ise iki kart arasında takas (swap) yapılır;
  // böylece her slot tam olarak bir karta atanmış kalır (aynı isimli paketlerde de).
  const handleChangeSlot = useCallback((uid: string, newSlotIndex: number) => {
    setScannedCards(prev => {
      const current = prev.find(c => c.cardUID === uid);
      const prevIdx = current?.assignedSlotIndex;
      const applySlot = (c: ScannedCard, idx: number): ScannedCard => {
        const slot = cardSlots?.[idx];
        const assignItem: CartItem | undefined = slot?.serviceItem
          ? {
              id: slot.serviceItem.id,
              productId: slot.serviceItem.id,
              name: slot.serviceItem.name,
              salePrice: slot.serviceItem.price,
              quantity: 1,
            }
          : undefined;
        return {
          ...c,
          assignedSlotIndex: idx,
          assignedServiceItem: assignItem,
          entitlements: slot?.entitlements || [],
          packageName: slot?.packageName,
        };
      };
      return prev.map(c => {
        if (c.cardUID === uid) return applySlot(c, newSlotIndex);
        if (c.assignedSlotIndex === newSlotIndex && prevIdx != null) return applySlot(c, prevIdx);
        return c;
      });
    });
  }, [cardSlots]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleScanCard();
    }
  };

  const handleAssign = async () => {
    if (scannedCards.length === 0) {
      setError('En az bir kart okutmalısınız');
      return;
    }

    // Paket slotları varsa: aynı slot iki karta atanmamalı (karışık paket senaryosu).
    if (cardSlots && cardSlots.length > 0) {
      const idxs = scannedCards.map(c => c.assignedSlotIndex).filter((i): i is number => i != null);
      if (new Set(idxs).size !== idxs.length) {
        setError('Aynı paket birden fazla bilekliğe atanmış. Lütfen her bilekliğe farklı paket seçin.');
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);

      const cards: CardAssignmentItemDto[] = scannedCards.map(sc => ({
        cardUID: sc.cardUID,
        cardLabel: sc.cardLabel,
        depositAmount: sc.resolvedDepositAmount,
        serviceProductId: sc.assignedServiceItem?.productId,
        serviceProductName: sc.assignedServiceItem?.name,
        // ✅ Ekran ayarındaki geçiş modunu kullan (sabit SINGLE_ENTRY yerine).
        // Şirket "sınırsız geçiş" (UNLIMITED_DAILY) seçtiyse karta da o yazılır.
        entryMode: entryMode || 'SINGLE_ENTRY',
        totalEntryRights: 1,
        entitlements: (sc.entitlements || []).map(e => ({
          productId: e.productId,
          productName: e.productName,
          quantity: e.quantity || 1,
          unitPricePaid: e.unitPricePaid || 0,
        })),
      }));

      const dto: AssignCardsDto = {
        saleReceiptId,
        saleReceiptNumber,
        pavoPaySaleId,
        pavoPaySaleNumber,
        screenId,
        paymentMethod,
        cards,
      };

      await clubEntryService.assignCards(dto);

      // İade/iptal listesinde kulüp giriş satışı kart numarasıyla görünsün/aransın.
      // Satış no biliniyorsa (POS) kesin; bilinmiyorsa (Rest-P2P fire-and-forget
      // nakit) son requestId üzerinden eşleştirilir.
      if (companyId) {
        linkClubEntryCard(
          companyId,
          cards.map(c => c.cardUID).filter(Boolean).join(', '),
          { pavoPaySaleNumber }
        );
      }

      // ✅ ZORUNLU: İçeride ön ödemeli okutma aktif CardSession gerektirir.
      // Depositosuz akışta da her kart için oturum aç (entitlement redemption için şart).
      if (companyId) {
        for (const sc of scannedCards) {
          try {
            const cardRes = await cardManagementService.getCardByUID(companyId, sc.cardUID);
            const cardObj = cardRes.data?.data || cardRes.data;
            const cardId = cardObj?.id || cardObj?.Id;
            if (!cardId) continue;
            await cardManagementService.openSession({
              CardId: cardId,
              AccountId: '0',
              AccountName: 'Kulüp Girişi',
              UserId: currentUserEmail || currentUser?.email || '',
              ScreenId: screenId != null ? String(screenId) : '',
            });
          } catch (sessionErr: any) {
            console.warn('[PostSaleCardModal] Oturum başlatma hatası:', sc.cardUID, sessionErr?.response?.data?.message || sessionErr?.message);
          }
        }
      }

      setSuccess(true);
      onComplete?.();

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('Kart atama hatası:', err);
      setError(err.response?.data?.message || 'Kart atama başarısız');
    } finally {
      setSaving(false);
    }
  };

  const totalDeposit = scannedCards.reduce((sum, c) => sum + c.resolvedDepositAmount, 0);
  const hasPosPayment = paymentMethod === 'POS' || paymentMethod === 'PARTIAL';

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NfcIcon />
          <Typography variant="h6">Kart Atama</Typography>
        </Box>
        <IconButton color="inherit" onClick={onClose} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, mt: 1 }}>
        {success ? (
          <Alert severity="success" sx={{ my: 2 }}>
            {scannedCards.length} kart başarıyla atandı!
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              {expectedCardCount} adet kart okutmanız gerekmektedir.
              {depositEnabled && ` Deposito tutarları kart tipine göre belirlenecektir.`}
            </Alert>

            {depositEnabled && hasPosPayment && (
              <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningAmberIcon />}>
                Deposito POS ile ödendi. Müşteriyi uyarın: Deposito iadesi yalnızca aynı banka/kredi kartıyla yapılabilir. Gün sonu yapılmadan iade alınmalıdır.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Kart UID okutun veya girin"
                value={cardInput}
                onChange={(e) => setCardInput(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                disabled={saving || scanning || scannedCards.length >= expectedCardCount}
                InputProps={{
                  startAdornment: <CreditCardIcon sx={{ mr: 1, color: 'action.active' }} />,
                  endAdornment: scanning ? <CircularProgress size={20} /> : undefined,
                }}
              />
              <Button
                variant="contained"
                onClick={handleScanCard}
                disabled={!cardInput.trim() || saving || scanning || scannedCards.length >= expectedCardCount}
              >
                Ekle
              </Button>
            </Box>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Okutulan Kartlar ({scannedCards.length}/{expectedCardCount})
            </Typography>

            <List dense>
              {scannedCards.map((card, index) => (
                <ListItem key={card.cardUID} sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 0.5 }}>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    secondaryTypographyProps={{ component: 'div' } as any}
                    primary={`Kart #${index + 1}: ${card.cardUID}`}
                    secondary={
                      cardSlots && cardSlots.length > 0 ? (
                        // ✅ Operatör hangi bilekliğe hangi paketi/ürünü atayacağını seçer.
                        <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                          <FormControl size="small" fullWidth sx={{ maxWidth: 360 }}>
                            <Select
                              value={card.assignedSlotIndex ?? ''}
                              onChange={(e) => handleChangeSlot(card.cardUID, Number(e.target.value))}
                              size="small"
                              displayEmpty
                              disabled={saving}
                              sx={{ fontSize: '0.78rem', '& .MuiSelect-select': { py: 0.5 } }}
                            >
                              {cardSlots.map((slot, i) => (
                                <MenuItem key={i} value={i} sx={{ fontSize: '0.78rem', whiteSpace: 'normal' }}>
                                  {slotLabel(slot, i)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      ) : (
                      <Box component="span" sx={{ display: 'block' }}>
                        <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                          {[
                            card.packageName ? `${card.packageName}:` : '',
                            card.assignedServiceItem?.name || 'Hizmet atanmadı',
                            card.cardTypeName ? `(${card.cardTypeName})` : '',
                          ].filter(Boolean).join(' ')}
                        </Typography>
                        {card.entitlements && card.entitlements.length > 0 && (
                          <Typography variant="caption" color="success.main" component="span" sx={{ display: 'block' }}>
                            🎁 {card.entitlements.map(e => `${e.productName}${e.quantity > 1 ? ` x${e.quantity}` : ''}`).join(', ')}
                          </Typography>
                        )}
                      </Box>
                      )
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {depositEnabled && card.resolvedDepositAmount > 0 && (
                        <Chip size="small" label={`₺${card.resolvedDepositAmount.toFixed(2)}`} color="warning" />
                      )}
                      <IconButton edge="end" onClick={() => handleRemoveCard(card.cardUID)} disabled={saving} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>

            {scannedCards.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                <NfcIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2">Kart okutmayı bekliyor...</Typography>
              </Box>
            )}

            {depositEnabled && scannedCards.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
                  <Typography variant="body2" color="text.secondary">Toplam Deposito:</Typography>
                  <Typography variant="body2" fontWeight="bold">₺{totalDeposit.toFixed(2)}</Typography>
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          {success ? 'Kapat' : 'İptal'}
        </Button>
        {!success && (
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={saving || scannedCards.length === 0}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            {saving ? 'Kaydediliyor...' : `${scannedCards.length} Kartı Ata`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PostSaleCardAssignmentModal;
